import React, { useState, useCallback, useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from 'utils/cn'

export interface RangeCalendarRange {
    start: Date | null
    end: Date | null
}

export interface RangeCalendarProps {
    value?: RangeCalendarRange
    defaultValue?: RangeCalendarRange
    onChange?: (range: RangeCalendarRange) => void
    months?: 1 | 2
    compact?: boolean
    minDate?: Date
    maxDate?: Date
    topContent?: React.ReactNode
    bottomContent?: React.ReactNode
    style?: React.CSSProperties
}

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const NAV_BUTTON_CLASS = 'flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-foreground hover:bg-default'

const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
const isBetween = (d: Date, s: Date, e: Date) => d > s && d < e

const getDays = (y: number, m: number) => {
    const first = new Date(y, m, 1).getDay()
    const dim = new Date(y, m + 1, 0).getDate()
    const pdim = new Date(y, m, 0).getDate()
    const days: { date: Date; current: boolean }[] = []
    for (let i = first - 1; i >= 0; i--) days.push({ date: new Date(y, m - 1, pdim - i), current: false })
    for (let i = 1; i <= dim; i++) days.push({ date: new Date(y, m, i), current: true })
    const rem = 42 - days.length
    for (let i = 1; i <= rem; i++) days.push({ date: new Date(y, m + 1, i), current: false })
    return days
}

const MonthGrid = ({
    year,
    month,
    range,
    today,
    onDayClick,
    minDate,
    maxDate,
    compact,
}: {
    year: number
    month: number
    range: RangeCalendarRange
    today: Date
    onDayClick: (d: Date) => void
    minDate?: Date
    maxDate?: Date
    compact: boolean
}) => {
    const days = useMemo(() => getDays(year, month), [year, month])
    return (
        <div className={compact ? 'flex w-56 flex-col' : 'flex w-[252px] flex-col'}>
            <div className="grid grid-cols-7">
                {DAYS.map((d) => (
                    <div key={d} className="flex h-7 items-center justify-center text-xs font-medium text-foreground-muted">
                        {d}
                    </div>
                ))}
            </div>
            <div className={compact ? 'grid grid-cols-7 gap-y-0.5' : 'grid grid-cols-7 gap-y-1'}>
                {days.map(({ date, current }, i) => {
                    const isStart = range.start ? isSameDay(date, range.start) : false
                    const isEnd = range.end ? isSameDay(date, range.end) : false
                    const inRange = range.start && range.end ? isBetween(date, range.start, range.end) : false
                    const isToday = isSameDay(date, today)
                    const dis = (minDate && date < minDate) || (maxDate && date > maxDate)
                    const column = i % 7
                    const showBand = !!range.start && !!range.end && !(isStart && isEnd) && (inRange || (isStart && column < 6) || (isEnd && column > 0))
                    return (
                        <div key={i} className={compact ? 'relative h-8 w-8' : 'relative h-9 w-9'}>
                            {showBand && (
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        'pointer-events-none absolute inset-y-0 bg-accent-soft',
                                        isStart ? 'left-1/2 right-0' : isEnd ? 'left-0 right-1/2' : 'inset-x-0',
                                        column === 0 && !isStart && 'rounded-l-full',
                                        column === 6 && !isEnd && 'rounded-r-full'
                                    )}
                                />
                            )}
                            <button
                                type="button"
                                aria-label={date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                aria-pressed={isStart || isEnd || !!inRange}
                                disabled={!!dis}
                                onClick={() => !dis && onDayClick(date)}
                                className={cn(
                                    'relative flex items-center justify-center rounded-full border-none p-0 transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                                    compact ? 'h-8 w-8 text-xs' : 'h-9 w-9 text-sm',
                                    isStart || isEnd
                                        ? 'bg-accent text-accent-foreground'
                                        : cn('bg-transparent text-foreground', !inRange && (isToday ? 'bg-default' : !current && 'text-foreground-muted')),
                                    dis ? 'cursor-default opacity-50' : 'cursor-pointer'
                                )}
                            >
                                {date.getDate()}
                            </button>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export const RangeCalendar = ({
    value: controlledValue,
    defaultValue = { start: null, end: null },
    onChange,
    months = 1,
    compact = false,
    minDate,
    maxDate,
    topContent,
    bottomContent,
    style,
}: RangeCalendarProps) => {
    const today = useMemo(() => new Date(), [])
    const [internalValue, setInternalValue] = useState<RangeCalendarRange>(defaultValue)
    const range = controlledValue ?? internalValue
    const [selecting, setSelecting] = useState<'start' | 'end'>('start')
    const [viewYear, setViewYear] = useState((range.start || range.end || today).getFullYear())
    const [viewMonth, setViewMonth] = useState((range.start || range.end || today).getMonth())

    const goToPrev = useCallback(() => {
        if (viewMonth === 0) {
            setViewMonth(11)
            setViewYear((y) => y - 1)
        } else setViewMonth((m) => m - 1)
    }, [viewMonth])

    const goToNext = useCallback(() => {
        if (viewMonth === 11) {
            setViewMonth(0)
            setViewYear((y) => y + 1)
        } else setViewMonth((m) => m + 1)
    }, [viewMonth])

    const handleDayClick = useCallback(
        (date: Date) => {
            let next: RangeCalendarRange
            if (selecting === 'start') {
                next = { start: date, end: null }
                setSelecting('end')
            } else {
                next = range.start && date < range.start ? { start: date, end: range.start } : { start: range.start, end: date }
                setSelecting('start')
            }
            if (!controlledValue) setInternalValue(next)
            onChange?.(next)
        },
        [selecting, range, controlledValue, onChange]
    )

    const monthLabel = (y: number, m: number) => new Date(y, m).toLocaleString('en-US', { month: 'long', year: 'numeric' })
    const m2 = viewMonth + 1 > 11 ? 0 : viewMonth + 1
    const y2 = viewMonth + 1 > 11 ? viewYear + 1 : viewYear

    return (
        <div className={cn('flex flex-col rounded-3xl bg-surface', compact ? 'p-0' : 'p-4')} style={style}>
            {topContent && <div className="mb-2">{topContent}</div>}
            <div className={cn('flex', months === 2 && 'gap-4')}>
                {/* Month 1 */}
                <div className="flex flex-col">
                    <div className="mb-3 flex items-center justify-between">
                        <button type="button" onClick={goToPrev} aria-label="Previous month" className={NAV_BUTTON_CLASS}>
                            <ChevronLeft size={16} strokeWidth={1.5} />
                        </button>
                        <span className="text-sm font-medium text-foreground">{monthLabel(viewYear, viewMonth)}</span>
                        {months === 1 ? (
                            <button type="button" onClick={goToNext} aria-label="Next month" className={NAV_BUTTON_CLASS}>
                                <ChevronRight size={16} strokeWidth={1.5} />
                            </button>
                        ) : (
                            <span className="w-7" />
                        )}
                    </div>
                    <MonthGrid
                        year={viewYear}
                        month={viewMonth}
                        range={range}
                        today={today}
                        onDayClick={handleDayClick}
                        minDate={minDate}
                        maxDate={maxDate}
                        compact={compact}
                    />
                </div>
                {/* Month 2 */}
                {months === 2 && (
                    <div className="flex flex-col">
                        <div className="mb-3 flex items-center justify-between">
                            <span className="w-7" />
                            <span className="text-sm font-medium text-foreground">{monthLabel(y2, m2)}</span>
                            <button type="button" onClick={goToNext} aria-label="Next month" className={NAV_BUTTON_CLASS}>
                                <ChevronRight size={16} strokeWidth={1.5} />
                            </button>
                        </div>
                        <MonthGrid
                            year={y2}
                            month={m2}
                            range={range}
                            today={today}
                            onDayClick={handleDayClick}
                            minDate={minDate}
                            maxDate={maxDate}
                            compact={compact}
                        />
                    </div>
                )}
            </div>
            {bottomContent && <div className="mt-2">{bottomContent}</div>}
        </div>
    )
}
